/**
 * 실행 하나를 통째로 — 노드별 상태·소요·메모리, 최초 실패, 노드 로그, 그리고 **실행 연결 고리**.
 *
 * 실행 노드가 부른 자식 실행(▸)과 이 실행을 부른 부모 실행(◂)을 눌러 이 창 안에서 오간다 — 「뒤로」 로 돌아온다.
 * 예전에는 자식 워크플로를 따로 열어 그 실행을 찾아 들어가야 했다(현장 2026-09-29).
 * 실행 탭(WindowRunHistory)과 디자이너 실행 이력(RunHistoryDialog)이 함께 쓴다. 서버: services/run_links.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchRunDetailByDag, fetchTaskLog, fmtMemory, fmtTaskRate, fmtTaskRows, memoryTitle, stateLabel, stateStyle, RunDetail, RunLink, TaskInfo,
} from '../../services/executionApi';
import { formatDateTime, formatDuration } from '../../utils/formatters';
import FirstFailureBanner, { NoNodeFailureNote } from './FirstFailureBanner';
import RunStateBadge from './RunStateBadge';
import ChildRunRows, { ChildToggle } from './ChildRunRows';

export interface RunRef {
  dagId: string;
  runId: string;
  /** 제목에 쓸 워크플로 이름 (모르면 dag id) */
  name?: string;
  /** 열자마자 로그를 보일 노드 */
  taskId?: string;
}

interface Props {
  start: RunRef;
  /** 노드 task_id → 화면 이름 (첫 실행에만 — 자식·부모로 옮기면 task_id 로 보인다) */
  labels?: Record<string, string>;
  onClose: () => void;
}

const RunDetailModal: React.FC<Props> = ({ start, labels, onClose }) => {
  const { t } = useTranslation();
  // 디자이너 실행 이력 창 위에 뜬다 — Esc 는 이 창만 닫아야 한다. 캡처 단계에서 받아 아래 창의 처리기로 가지 않게 막는다
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[data-app-dialog]')) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [onClose]);
  const [stack, setStack] = useState<RunRef[]>([start]);
  const cur = stack[stack.length - 1];
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [task, setTask] = useState<TaskInfo | null>(null);
  const [log, setLog] = useState<{ text: string; loading: boolean; attempt: number } | null>(null);
  /** 펼친 실행 노드 — 자식 실행의 노드들을 그 아래로 */
  const [treeOpen, setTreeOpen] = useState<Record<string, boolean>>({});
  const labelOf = (id: string) => (stack.length === 1 && labels?.[id]) || id;

  /** 노드 로그 — 시도(attempt)를 고르지 않으면 마지막 시도. 재시도한 노드는 앞 시도의 로그도 볼 수 있다 */
  const openLog = useCallback(async (ref: RunRef, tk: TaskInfo, attempt?: number) => {
    const n = attempt || tk.try_number || 1;
    setTask(tk);
    setLog({ text: '', loading: true, attempt: n });
    try {
      setLog({ text: await fetchTaskLog(ref.dagId, ref.runId, tk.task_id, n), loading: false, attempt: n });
    } catch (e) {
      setLog({ text: t('execution:detail.logFailed', { message: (e as Error).message }), loading: false, attempt: n });
    }
  }, [t]);

  useEffect(() => {
    let alive = true;
    setDetail(null); setError(null); setTask(null); setLog(null); setTreeOpen({});
    fetchRunDetailByDag(cur.dagId, cur.runId)
      .then((d) => {
        if (!alive) return;
        setDetail(d);
        const want = cur.taskId || (d.firstFailure?.task_id ?? null);
        const tk = want ? d.tasks.find((x) => x.task_id === want) : undefined;
        if (tk) openLog(cur, tk);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [cur, openLog]);

  const go = (link: RunLink, taskId?: string) => setStack((s) => [...s, { dagId: link.dag_id, runId: link.run_id, name: link.name || link.dag_id, taskId }]);
  const goRef = (ref: RunRef) => setStack((s) => [...s, ref]);
  const back = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="bg-white rounded-lg shadow-2xl w-[1100px] max-w-[96vw] h-[82vh] flex flex-col overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        {/* 머리 — 어느 실행인지, 어디서 왔는지 */}
        <div className="flex items-center gap-2 px-4 py-2 border-b shrink-0 text-sm">
          {stack.length > 1 && (
            <button type="button" className="px-2 py-0.5 text-xs border rounded hover:bg-gray-100" onClick={back} title={t('execution:detail.backTitle')}>
              {t('execution:detail.back')}
            </button>
          )}
          <span className="font-semibold text-gray-800 truncate">{cur.name || cur.dagId}</span>
          {detail && <RunStateBadge state={detail.state} handled={detail.handledFailure} className="px-2 py-0.5 rounded-full text-xs font-semibold" />}
          <span className="text-xs text-gray-400 font-mono truncate" title={cur.runId}>{cur.runId}</span>
          <button type="button" className="ml-auto px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('execution:detail.close')}>✕</button>
        </div>
        {/* 거쳐 온 길 — A › B › C */}
        {stack.length > 1 && (
          <div className="px-4 py-1 text-[0.6875rem] text-gray-500 border-b shrink-0 truncate">
            {stack.map((r, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="mx-1 text-gray-300">›</span>}
                <button type="button" className={i === stack.length - 1 ? 'font-semibold text-gray-700' : 'hover:underline'} onClick={() => setStack((s) => s.slice(0, i + 1))}>{r.name || r.dagId}</button>
              </React.Fragment>
            ))}
          </div>
        )}

        {error ? <div className="p-4 text-sm text-red-600">{error}</div> : !detail ? <div className="p-4 text-sm text-gray-500">{t('common:ui.loading')}</div> : (
          <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            {/* 왼쪽: 노드 목록 */}
            <div className="min-h-0 overflow-auto border-r">
              <div className="px-3 py-1.5 text-xs text-gray-600 border-b">
                {formatDateTime(detail.startDate)} ~ {detail.endDate ? formatDateTime(detail.endDate) : t('execution:detail.inProgress')}
              </div>
              {detail.parent && (
                <div className="mx-2 my-1.5 px-2.5 py-1.5 rounded border border-blue-200 bg-blue-50 text-xs text-gray-700 flex items-center gap-2">
                  <span className="shrink-0 text-blue-700 font-semibold">{t('execution:detail.calledBy')}</span>
                  <span className="truncate">{detail.parent.name || detail.parent.dag_id} › {detail.parent.task_id}</span>
                  <button type="button" className="ml-auto shrink-0 text-blue-700 hover:underline" onClick={() => go(detail.parent as RunLink, detail.parent?.task_id)}>{t('execution:detail.openParent')}</button>
                </div>
              )}
              {detail.firstFailure
                ? <FirstFailureBanner failure={detail.firstFailure} label={labelOf(detail.firstFailure.task_id)}
                    onOpenLog={() => { const tk = detail.tasks.find((x) => x.task_id === detail.firstFailure?.task_id); if (tk) openLog(cur, tk); }} />
                : detail.state === 'failed' ? <NoNodeFailureNote /> : null}
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 text-gray-500">
                  <tr className="text-left">
                    <th className="px-2 py-1 font-medium">{t('execution:detail.node')}</th>
                    <th className="px-2 py-1 font-medium">{t('execution:detail.state')}</th>
                    <th className="px-2 py-1 font-medium text-right">{t('execution:detail.duration')}</th>
                    <th className="px-2 py-1 font-medium text-right" title={t('execution:rows.colTitle')}>{t('execution:rows.col')}</th>
                    <th className="px-2 py-1 font-medium text-right" title={t('execution:rows.rateTitle')}>{t('execution:rows.rateCol')}</th>
                    <th className="px-2 py-1 font-medium text-right">{t('execution:mem.col')}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.tasks.map((tk) => (
                    <React.Fragment key={tk.task_id}>
                    <tr className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${task?.task_id === tk.task_id ? 'bg-blue-50' : ''}`}
                      onClick={() => openLog(cur, tk)} title={t('execution:detail.clickForLog')}>
                      <td className="px-2 py-1">
                        <div className="text-gray-800 truncate max-w-[16rem]" title={tk.task_id}>
                          {tk.child && <ChildToggle open={!!treeOpen[tk.task_id]} onToggle={() => setTreeOpen((p) => ({ ...p, [tk.task_id]: !p[tk.task_id] }))} />}
                          {labelOf(tk.task_id)}
                        </div>
                        {tk.child && (
                          <button type="button" className="mt-0.5 text-[0.6875rem] text-blue-700 hover:underline text-left"
                            onClick={(e) => { e.stopPropagation(); go(tk.child as RunLink); }} title={tk.child.run_id}>
                            {t('execution:detail.openChild', { name: tk.child.name || tk.child.dag_id })}
                            {tk.child.state ? <span className={`ml-1 px-1 rounded ${stateStyle(tk.child.state)}`}>{stateLabel(tk.child.state)}</span> : null}
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-1"><span className={`px-1.5 rounded ${stateStyle(tk.state)}`}>{stateLabel(tk.state)}</span></td>
                      <td className="px-2 py-1 text-right text-gray-500 tabular-nums">{tk.duration != null ? formatDuration(Number(tk.duration)) : '-'}</td>
                      <td className="px-2 py-1 text-right text-gray-500 tabular-nums">{fmtTaskRows(tk)}</td>
                      <td className="px-2 py-1 text-right text-gray-500 tabular-nums">{fmtTaskRate(tk)}</td>
                      <td className="px-2 py-1 text-right text-gray-500 tabular-nums" title={memoryTitle(tk.memory)}>{fmtMemory(tk.memory)}</td>
                    </tr>
                    {tk.child && treeOpen[tk.task_id] && <ChildRunRows link={tk.child} depth={1} onOpen={goRef} />}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {/* 오른쪽: 로그 */}
            <div className="min-h-0 flex flex-col">
              <div className="px-3 py-1.5 text-xs border-b text-gray-600 flex items-center gap-2 shrink-0">
                {task ? <><span className="font-semibold text-gray-800 truncate">{labelOf(task.task_id)}</span>
                  {/* 재시도한 노드 — 시도마다 로그가 따로 있다. 앞 시도의 오류가 원인인 경우가 많다 */}
                  {(task.try_number || 1) > 1 && (
                    <span className="flex items-center gap-1">
                      <span className="text-gray-400">{t('execution:detail.attempt')}</span>
                      {Array.from({ length: task.try_number || 1 }, (_, i) => i + 1).map((n) => (
                        <button key={n} type="button" onClick={() => openLog(cur, task, n)}
                          className={`px-1.5 rounded border ${log?.attempt === n ? 'border-blue-500 text-blue-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>{n}</button>
                      ))}
                    </span>
                  )}
                  <button type="button" className="ml-auto text-blue-700 hover:underline" onClick={() => openLog(cur, task, log?.attempt)}>{t('execution:detail.reload')}</button></>
                  : <span>{t('execution:detail.pickNode')}</span>}
              </div>
              <pre className="flex-1 min-h-0 overflow-auto p-3 text-[0.6875rem] leading-relaxed whitespace-pre-wrap break-words font-mono bg-gray-50 text-gray-800">
                {log?.loading ? t('common:ui.loading') : log?.text || ''}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunDetailModal;
